/**
 * The password rules, mirrored from backend/app/core/password_policy.py so a
 * form can say what is wrong before it submits. The API is the authority - it
 * applies the same rules and its message is shown if the two ever disagree.
 */

export const PASSWORD_MIN_LENGTH = 12
const PASSPHRASE_LENGTH = 16
const MAX_LENGTH = 200

export const PASSWORD_HINT = `At least ${PASSWORD_MIN_LENGTH} characters mixing upper and lower case, numbers and symbols - or a passphrase of ${PASSPHRASE_LENGTH} or more characters.`

const COMMON = new Set([
  'password1234', 'password12345', 'passw0rd1234', 'p@ssw0rd1234', 'p@ssword1234', 'password@123',
  'password@1234', '123456789012', '1234567890ab', 'qwertyuiop12', 'qwerty123456', 'qwertyqwerty',
  '1q2w3e4r5t6y', 'asdfghjkl123', 'zxcvbnm12345', 'letmein12345', 'letmeinplease', 'welcome12345',
  'welcome@1234', 'iloveyou1234', 'admin1234567', 'admin@123456', 'administrator', 'changeme1234',
  'abcdefghijkl', 'abc123456789', 'trustno1trustno1', 'football1234', 'baseball1234', 'sunshine1234',
  'princess1234', 'dragon123456', 'monkey123456', 'master123456', 'superman1234', 'passwordpassword',
  'test12345678', 'guest1234567', 'default12345', 'secret123456', 'company12345', 'insuronix123',
  'insuronix1234', 'insuronix2026',
])

const BASE_WORDS = new Set([
  'password', 'passw', 'qwerty', 'welcome', 'letmein', 'admin', 'insuronix', 'iloveyou', 'changeme', 'secret',
])

/** The first rule a password breaks, as a sentence - or null when it is acceptable. */
export function passwordProblem(password: string, email?: string | null): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) return `Use at least ${PASSWORD_MIN_LENGTH} characters.`
  if (password.length > MAX_LENGTH) return `Use at most ${MAX_LENGTH} characters.`

  const lower = password.toLowerCase()
  const letters = lower.replace(/[^a-z]/g, '')
  if (COMMON.has(lower) || (BASE_WORDS.has(letters) && letters.length >= lower.length - 6)) {
    return 'That password is too common. Choose something less guessable.'
  }
  if (new Set(password).size < 5) return 'Use a greater variety of characters.'

  const local = (email ?? '').split('@')[0].toLowerCase()
  if (local.length >= 4 && lower.includes(local)) return 'Do not include your email address in your password.'

  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(password)).length
  if (password.length < PASSPHRASE_LENGTH && classes < 3) {
    return 'Mix at least three of lowercase, uppercase, numbers and symbols - or use a passphrase of 16 or more characters.'
  }
  return null
}
